import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import StatusPill from '@/components/marketplace/StatusPill';
import SEOScoreBadge from '@/components/marketplace/SEOScoreBadge';
import { toast } from 'sonner';
import { Check, X, RotateCcw, Ban, ShieldCheck, BadgeCheck, Eye, Flag, Pencil, AlertTriangle, Search } from 'lucide-react';

const PriorityPill = ({ priority }) => {
  if (priority === 'urgent') {
    return <span className='inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-800' title='Urgent: reported by many users or flagged by moderation engine' data-testid='mod-priority-urgent'><AlertTriangle className='h-2.5 w-2.5' /> Urgent</span>;
  }
  if (priority === 'high') {
    return <span className='inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800' title='High: reported OR flagged OR new seller OR fresh submission' data-testid='mod-priority-high'>High</span>;
  }
  return <span className='inline-flex items-center rounded-full border bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground' data-testid='mod-priority-normal'>Normal</span>;
};

const relativeTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const QUEUES = [
  { key: 'pending_review', label: 'Pending' },
  { key: 'live', label: 'Live' },
  { key: 'sold', label: 'Sold' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'suspended', label: 'Suspended' },
];

const VALID_TABS = new Set(QUEUES.map(q => q.key));

const ModeratorPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Item 6 — the active tab lives in the URL so it survives a refresh AND so
  // the shared edit screen can return the moderator to the exact same tab.
  const initialTab = VALID_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : 'pending_review';
  const [tab, setTab] = useState(initialTab);
  const changeTab = (v) => {
    setSearch('');
    setTab(v);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', v); return p; }, { replace: true });
  };
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reports, setReports] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [reveals, setReveals] = useState([]);
  const [spamInquiries, setSpamInquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [decisionModal, setDecisionModal] = useState({ open: false, action: '', listing: null, reason: '' });
  // Staff-only "Revert Sold Listing" dialog. Backend REQUIRES a reason when
  // staff reverts (see listings_router.revert_sold_listing), so we capture
  // it here rather than using an empty prompt.
  const [revertSoldModal, setRevertSoldModal] = useState({ open: false, listing: null, reason: '' });
  const [revertingSold, setRevertingSold] = useState(false);
  // Verification rejection modal — reason MUST be supplied so the user
  // knows what to fix on re-submission (backend enforces the same rule).
  const [verifyRejectModal, setVerifyRejectModal] = useState({ open: false, id: '', reason: '' });
  const [verifyHistory, setVerifyHistory] = useState({ userId: '', docs: [], loading: false });
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [search, setSearch] = useState('');

  const MOD_PAGE_SIZE = 50;

  const loadListings = async (key = tab, searchVal = search) => {
    setLoading(true);
    try {
      const { data } = await api.get('/listings/mod/queue', { params: { status: key, search: searchVal || undefined, page: 1, limit: MOD_PAGE_SIZE } });
      setListings(data.items || []);
      setTotal(data.total || 0);
      setPage(1);
    } finally { setLoading(false); }
  };

  // Fetch the next page and APPEND, so the moderator can page through the
  // full queue (scales to thousands of listings) instead of being capped.
  const loadMore = async () => {
    const next = page + 1;
    setLoadingMore(true);
    try {
      const { data } = await api.get('/listings/mod/queue', { params: { status: tab, search: search || undefined, page: next, limit: MOD_PAGE_SIZE } });
      setListings(prev => {
        const seen = new Set(prev.map(x => x.id));
        return [...prev, ...(data.items || []).filter(x => !seen.has(x.id))];
      });
      setTotal(data.total || 0);
      setPage(next);
    } finally { setLoadingMore(false); }
  };

  // Reload after a moderation action WITHOUT losing the moderator's place:
  // re-fetch the same-sized window they had already scrolled through.
  const reloadCurrent = async () => {
    const limit = Math.max(MOD_PAGE_SIZE, listings.length);
    try {
      const { data } = await api.get('/listings/mod/queue', { params: { status: tab, search: search || undefined, page: 1, limit } });
      setListings(data.items || []);
      setTotal(data.total || 0);
      setPage(Math.max(1, Math.ceil((data.items || []).length / MOD_PAGE_SIZE)));
    } catch (_) { /* keep current view on failure */ }
  };

  const loadSide = async () => {
    const [r, v, l, s, sp] = await Promise.all([
      api.get('/reports', { params: { status: 'open' } }),
      api.get('/verification/queue'),
      api.get('/inquiries/reveal/all', { params: { limit: 20 } }),
      api.get('/admin/stats'),
      api.get('/inquiries/spam', { params: { limit: 20 } }),
    ]);
    setReports(r.data);
    setVerifications(v.data);
    setReveals(l.data);
    setStats(s.data);
    setSpamInquiries(sp.data);
  };

  // Debounced load on tab OR search change (300ms) so typing filters the
  // queue server-side without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { loadListings(tab, search); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search]);
  useEffect(() => { loadSide(); }, []);

  const decide = async () => {
    try {
      await api.post(`/listings/${decisionModal.listing.id}/moderate`, {
        action: decisionModal.action,
        reason: decisionModal.reason,
      });
      toast.success(`Listing ${decisionModal.action}d`);
      setDecisionModal({ open: false, action: '', listing: null, reason: '' });
      reloadCurrent();
      loadSide();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const confirmRevertSold = async () => {
    const reason = (revertSoldModal.reason || '').trim();
    if (!reason) {
      toast.error('Please add a reason — required when staff reverts a sold listing');
      return;
    }
    setRevertingSold(true);
    try {
      await api.post(`/listings/${revertSoldModal.listing.id}/revert-sold`, { reason });
      toast.success('Listing reverted to Live');
      setRevertSoldModal({ open: false, listing: null, reason: '' });
      reloadCurrent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revert failed');
    } finally {
      setRevertingSold(false);
    }
  };

  // Full document history per user — reviewed docs stay reachable here even
  // after they leave the pending queue.
  const toggleVerificationHistory = async (userId) => {
    if (verifyHistory.userId === userId) {
      setVerifyHistory({ userId: '', docs: [], loading: false });
      return;
    }
    setVerifyHistory({ userId, docs: [], loading: true });
    try {
      const { data } = await api.get(`/verification/user/${userId}`);
      setVerifyHistory({ userId, docs: data, loading: false });
    } catch {
      setVerifyHistory({ userId: '', docs: [], loading: false });
      toast.error('Could not load document history');
    }
  };

  const reviewVerification = async (id, approve) => {
    // For approvals we can call the API immediately. Rejections open a
    // dedicated modal (see verifyRejectModal) so the moderator is forced
    // to supply a reason — the backend also enforces this.
    if (!approve) {
      setVerifyRejectModal({ open: true, id, reason: '' });
      return;
    }
    try {
      await api.post(`/verification/${id}/review`, { approve: true, reason: '' });
      toast.success('Approved');
      loadSide();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const confirmVerifyReject = async () => {
    const reason = (verifyRejectModal.reason || '').trim();
    if (!reason) {
      toast.error('Please provide a rejection reason so the user knows what to fix.');
      return;
    }
    setVerifyBusy(true);
    try {
      await api.post(`/verification/${verifyRejectModal.id}/review`, { approve: false, reason });
      toast.success('Rejected — reason shared with user');
      setVerifyRejectModal({ open: false, id: '', reason: '' });
      loadSide();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setVerifyBusy(false);
    }
  };

  const resolveReport = async (id, action) => {
    try {
      await api.post(`/reports/${id}/resolve?action=${action}`);
      toast.success('Report resolved');
      loadSide();
    } catch { toast.error('Failed'); }
  };

  const restoreSpamInquiry = async (id) => {
    try {
      await api.post(`/inquiries/${id}/status?status=new`);
      toast.success('Restored to seller inbox');
      loadSide();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>Moderator Panel</h1>
        <p className='text-muted-foreground text-sm'>Review listings, resolve reports, approve verifications.</p>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        {[
          ['Pending listings', stats.pending_listings, ShieldCheck],
          ['Open reports', stats.open_reports, Flag],
          ['Verifications', stats.pending_verifications, BadgeCheck],
          ['Total reveals', stats.reveals, Eye],
        ].map(([k, v, Icon]) => (
          <Card key={k} className='p-4'>
            <div className='flex items-start justify-between'>
              <div>
                <div className='text-xs uppercase tracking-wider text-muted-foreground'>{k}</div>
                <div className='text-2xl font-semibold tabular-nums mt-1'>{v ?? 0}</div>
              </div>
              <Icon className='h-4 w-4 text-primary' />
            </div>
          </Card>
        ))}
      </div>

      <div className='grid lg:grid-cols-3 gap-6'>
        <div className='lg:col-span-2 space-y-3'>
          <Card className='p-4'>
            <Tabs value={tab} onValueChange={changeTab}>
              <TabsList data-testid='moderator-tabs'>
                {QUEUES.map(q => <TabsTrigger key={q.key} value={q.key} data-testid={`mod-tab-${q.key}`}>{q.label}</TabsTrigger>)}
              </TabsList>
            </Tabs>
            <div className='relative mt-3'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder='Search this queue — title, city, listing ID, or owner name / email / company'
                className='pl-9'
                data-testid='mod-search-input'
              />
              {search && (
                <button
                  type='button'
                  onClick={() => setSearch('')}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
                  data-testid='mod-search-clear'
                  aria-label='Clear search'
                >
                  <X className='h-4 w-4' />
                </button>
              )}
            </div>
            <div className='mt-3'>
              {loading ? (
                <div className='py-10 text-center text-muted-foreground'>Loading…</div>
              ) : listings.length === 0 ? (
                <div className='py-10 text-center text-muted-foreground'>{search ? 'No listings match your search.' : 'Queue is empty.'}</div>
              ) : (
                <div className='space-y-2' data-testid='moderator-listings-table'>
                  {listings.map(l => (
                    <div
                      key={l.id}
                      className={`rounded-lg border p-3 hover:bg-secondary/40 transition-colors ${l.priority === 'urgent' ? 'bg-red-50/30 border-red-200' : ''}`}
                      data-testid='moderator-listings-row'
                    >
                      {/* Info section — full width. */}
                      <div className='min-w-0'>
                        <div className='flex items-start justify-between gap-2 flex-wrap'>
                          <Link
                            to={`/listing/${l.id}`}
                            target='_blank'
                            className='font-medium hover:underline line-clamp-2 text-sm flex-1 min-w-0'
                          >
                            {l.title}
                          </Link>
                          <div className='flex items-center gap-1.5 flex-wrap shrink-0'>
                            <PriorityPill priority={l.priority} />
                            <StatusPill status={l.status} />
                            {l.open_reports_count > 0 && (
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${l.open_reports_count >= 3 ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}
                                data-testid={`mod-reports-${l.id}`}
                              >
                                <Flag className='h-2.5 w-2.5' /> {l.open_reports_count}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className='flex items-center gap-2 mt-1 flex-wrap text-[11px] text-muted-foreground'>
                          <span className='uppercase font-medium'>{l.listing_type}</span>
                          {l.category_name && (<><span>·</span><span>{l.category_name}{l.subcategory_name ? ` / ${l.subcategory_name}` : ''}</span></>)}
                          <span>·</span>
                          <span title={l.created_at}>{relativeTime(l.created_at)}</span>
                          <SEOScoreBadge score={l.seo_score} summary={l.seo_score_summary} />
                        </div>

                        <div className='mt-1 flex items-center gap-1.5 flex-wrap text-[11px]'>
                          <span className='font-medium' data-testid={`mod-owner-${l.id}`}>{l.owner_name || '—'}</span>
                          {(l.owner_company || l.owner_email) && (
                            <span className='text-muted-foreground truncate max-w-[220px]'>({l.owner_company || l.owner_email})</span>
                          )}
                          <span className='inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] uppercase font-medium bg-secondary'>{l.owner_tier}</span>
                          {l.owner_gst_verified && (
                            <span className='inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-1 py-0.5 text-[9px]'><ShieldCheck className='h-2 w-2' /> GST</span>
                          )}
                          <span className='text-[10px] text-muted-foreground'>· {l.owner_total_listings || 0} listings</span>
                        </div>

                        {Array.isArray(l.flagged_reasons) && l.flagged_reasons.length > 0 && (
                          <div className='flex flex-wrap gap-1 mt-2' data-testid={`mod-flags-${l.id}`}>
                            {l.flagged_reasons.map(fr => (
                              <span
                                key={fr}
                                className='inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800'
                                title='Content flagged by moderation engine — requires review'
                              >
                                <Flag className='h-2.5 w-2.5' /> {fr.replace('soft.', '').replace('hard.', '').replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Actions row — ALWAYS below the info section so it can
                          never collide with content and never get clipped by an
                          ancestor. Uses `flex-wrap` + `w-full` so buttons stack
                          onto multiple rows on narrow screens if needed, and
                          remain fully clickable at every resolution. */}
                      <div
                        className='mt-3 pt-3 border-t border-border/60 flex flex-wrap gap-1.5 w-full'
                        data-testid={`mod-actions-${l.id}`}
                      >
                        <Link to={`/post/${l.id}/edit?from=moderator&tab=${tab}`} data-testid={`mod-edit-${l.id}`}>
                          <Button size='sm' variant='outline' className='h-8' title='Edit listing'>
                            <Pencil className='h-3.5 w-3.5 mr-1' /> Edit
                          </Button>
                        </Link>
                        {tab === 'pending_review' && (
                          <>
                            <Button size='sm' variant='outline' className='h-8 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 border-emerald-300' onClick={() => setDecisionModal({ open: true, action: 'approve', listing: l, reason: '' })} data-testid={`mod-approve-${l.id}`}>
                              <Check className='h-3.5 w-3.5 mr-1' /> Approve
                            </Button>
                            <Button size='sm' variant='outline' className='h-8 text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-300' onClick={() => setDecisionModal({ open: true, action: 'needs_correction', listing: l, reason: '' })} data-testid={`mod-correct-${l.id}`}>
                              <RotateCcw className='h-3.5 w-3.5 mr-1' /> Correct
                            </Button>
                            <Button size='sm' variant='outline' className='h-8 text-destructive hover:text-destructive hover:bg-red-50 border-red-300' onClick={() => setDecisionModal({ open: true, action: 'reject', listing: l, reason: '' })} data-testid={`mod-reject-${l.id}`}>
                              <X className='h-3.5 w-3.5 mr-1' /> Reject
                            </Button>
                          </>
                        )}
                        {tab === 'live' && (
                          <Button size='sm' variant='outline' className='h-8' onClick={() => setDecisionModal({ open: true, action: 'suspend', listing: l, reason: '' })} data-testid={`mod-suspend-${l.id}`}>
                            <Ban className='h-3.5 w-3.5 mr-1' /> Suspend
                          </Button>
                        )}
                        {tab === 'sold' && (
                          <Button size='sm' variant='outline' className='h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50' onClick={() => setRevertSoldModal({ open: true, listing: l, reason: '' })} data-testid={`mod-revert-sold-${l.id}`}>
                            <RotateCcw className='h-3.5 w-3.5 mr-1' /> Revert to Live
                          </Button>
                        )}
                        {(tab === 'suspended' || tab === 'rejected') && (
                          <Button size='sm' variant='outline' className='h-8' onClick={() => setDecisionModal({ open: true, action: 'approve', listing: l, reason: 'Reinstated' })} data-testid={`mod-reinstate-${l.id}`}>
                            <Check className='h-3.5 w-3.5 mr-1' /> Reinstate
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {listings.length < total && (
                    <div className='pt-3 flex flex-col items-center gap-2'>
                      <span className='text-xs text-muted-foreground' data-testid='mod-listings-count'>Showing {listings.length} of {total}</span>
                      <Button variant='outline' size='sm' onClick={loadMore} disabled={loadingMore} data-testid='mod-load-more'>
                        {loadingMore ? 'Loading…' : `Load more (${total - listings.length} remaining)`}
                      </Button>
                    </div>
                  )}
                  {listings.length >= total && total > MOD_PAGE_SIZE && (
                    <div className='pt-3 text-center text-xs text-muted-foreground' data-testid='mod-listings-count'>All {total} shown</div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className='space-y-3'>
          <Card className='p-4'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-semibold flex items-center gap-2'><Flag className='h-4 w-4' /> Open reports</div>
              <span className='text-xs text-muted-foreground'>{reports.length}</span>
            </div>
            {reports.length === 0 ? (
              <div className='text-sm text-muted-foreground'>No open reports.</div>
            ) : reports.slice(0, 8).map(r => (
              <div key={r.id} className='border-b last:border-b-0 py-2'>
                <div className='text-sm'><span className='font-medium'>{r.reason}</span> · {r.target_type}</div>
                {r.target && r.target.title && <Link to={`/listing/${r.target.id}`} target='_blank' className='text-xs text-primary hover:underline'>{r.target.title}</Link>}
                {r.details && <div className='text-xs text-muted-foreground mt-1'>{r.details}</div>}
                <div className='flex gap-1 mt-1'>
                  <Button size='sm' variant='ghost' onClick={() => resolveReport(r.id, 'dismiss')}>Dismiss</Button>
                  <Button size='sm' variant='ghost' onClick={() => resolveReport(r.id, 'suspend_listing')}>Suspend</Button>
                </div>
              </div>
            ))}
          </Card>

          <Card className='p-4'>
            <div className='font-semibold mb-3 flex items-center gap-2'><BadgeCheck className='h-4 w-4' /> Verification queue</div>
            {verifications.length === 0 ? (
              <div className='text-sm text-muted-foreground'>Nothing pending.</div>
            ) : verifications.slice(0, 8).map(v => (
              <div key={v.id} className='border-b last:border-b-0 py-2'>
                <div className='text-sm font-medium'>{v.user_name}</div>
                <div className='text-xs text-muted-foreground'>{v.document_type} · {v.user_email}</div>
                <div className='flex gap-1 mt-1 items-center flex-wrap'>
                  <a href={v.document_url} target='_blank' rel='noreferrer' className='text-xs text-primary hover:underline'>View doc</a>
                  <Button size='sm' variant='ghost' onClick={() => reviewVerification(v.id, true)} data-testid={`mod-verify-approve-${v.id}`}>Approve</Button>
                  <Button size='sm' variant='ghost' onClick={() => reviewVerification(v.id, false)} data-testid={`mod-verify-reject-${v.id}`}>Reject</Button>
                  <Button size='sm' variant='ghost' onClick={() => toggleVerificationHistory(v.user_id)} data-testid={`mod-verify-history-${v.id}`}>
                    {verifyHistory.userId === v.user_id ? 'Hide history' : 'All documents'}
                  </Button>
                </div>
                {verifyHistory.userId === v.user_id && (
                  <div className='mt-1 rounded-md border bg-secondary/30 p-2 space-y-1' data-testid={`mod-verify-history-list-${v.id}`}>
                    {verifyHistory.loading ? (
                      <div className='text-xs text-muted-foreground'>Loading…</div>
                    ) : verifyHistory.docs.length === 0 ? (
                      <div className='text-xs text-muted-foreground'>No documents found.</div>
                    ) : verifyHistory.docs.map(d => (
                      <div key={d.id} className='flex items-center justify-between gap-2 text-xs'>
                        <span className='min-w-0 truncate'>
                          {d.document_type} · {d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN') : '—'} · <span className='uppercase'>{d.status}</span>
                        </span>
                        {d.document_url && (
                          <a href={d.document_url} target='_blank' rel='noreferrer' className='text-primary hover:underline shrink-0'>View</a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Card>

          <Card className='p-4' data-testid='mod-spam-inquiries-card'>
            <div className='flex items-center justify-between mb-3'>
              <div className='font-semibold flex items-center gap-2'><Ban className='h-4 w-4 text-red-600' /> Spam inquiries</div>
              <span className='text-xs text-muted-foreground'>{spamInquiries.length}</span>
            </div>
            {spamInquiries.length === 0 ? (
              <div className='text-sm text-muted-foreground'>No spam-marked inquiries.</div>
            ) : spamInquiries.slice(0, 8).map(q => (
              <div key={q.id} className='border-b last:border-b-0 py-2' data-testid={`mod-spam-row-${q.id}`}>
                <div className='text-sm'><span className='font-medium'>{q.inquirer_name}</span> · {q.inquirer_email}</div>
                {q.listing_title && <Link to={`/listing/${q.listing_id}`} target='_blank' className='text-xs text-primary hover:underline'>{q.listing_title}</Link>}
                {q.message && <div className='text-xs text-muted-foreground mt-1 line-clamp-2'>{q.message}</div>}
                <div className='flex gap-1 mt-1'>
                  <Button size='sm' variant='ghost' onClick={() => restoreSpamInquiry(q.id)} data-testid={`mod-spam-restore-${q.id}`}>Not spam — restore</Button>
                </div>
              </div>
            ))}
          </Card>

          <Card className='p-4'>
            <div className='font-semibold mb-3 flex items-center gap-2'><Eye className='h-4 w-4' /> Recent reveals</div>
            {reveals.length === 0 ? <div className='text-sm text-muted-foreground'>No reveals yet.</div> :
              reveals.slice(0, 8).map(r => (
                <div key={r.id} className='border-b last:border-b-0 py-2 text-xs'>
                  <div><span className='font-medium'>{r.user_name}</span> revealed on {r.listing_title}</div>
                  <div className='text-muted-foreground'>{new Date(r.created_at).toLocaleString()}</div>
                </div>
              ))
            }
          </Card>
        </div>
      </div>

      <Dialog open={decisionModal.open} onOpenChange={(o) => setDecisionModal(s => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{decisionModal.action?.replace(/_/g, ' ')} listing</DialogTitle>
            <DialogDescription>
              Add a reason so the seller (and future moderators) understand why this decision was made.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='text-sm text-muted-foreground'>{decisionModal.listing?.title}</div>
            <div>
              <Label>Reason / notes</Label>
              <Textarea rows={4} value={decisionModal.reason} onChange={(e) => setDecisionModal(s => ({ ...s, reason: e.target.value }))} placeholder='Why?' data-testid='mod-decision-reason' />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDecisionModal(s => ({ ...s, open: false }))}>Cancel</Button>
            <Button onClick={decide} data-testid='mod-decision-confirm'>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert sold listing (staff only). Reason is REQUIRED by backend. */}
      <Dialog open={revertSoldModal.open} onOpenChange={(o) => !o && setRevertSoldModal({ open: false, listing: null, reason: '' })}>
        <DialogContent data-testid='mod-revert-sold-dialog'>
          <DialogHeader>
            <DialogTitle>Revert sold listing back to Live</DialogTitle>
            <DialogDescription>
              Reverting overrides the seller&apos;s closure. The reason you enter here is stored in the listing&apos;s audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='text-sm text-muted-foreground'>
              <strong className='text-foreground'>{revertSoldModal.listing?.title}</strong>
            </div>
            <div className='rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3 text-xs'>
              This overrides the seller&apos;s closure. Please add a justifiable reason — it&apos;s logged in the listing&apos;s audit trail (sold_revert_history).
            </div>
            <div>
              <Label>Reason (required)</Label>
              <Textarea
                rows={4}
                value={revertSoldModal.reason}
                onChange={(e) => setRevertSoldModal(s => ({ ...s, reason: e.target.value }))}
                placeholder='e.g. Seller contacted support saying the sale fell through; deal did not go through; wrong listing marked; buyer withdrew.'
                data-testid='mod-revert-sold-reason'
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setRevertSoldModal({ open: false, listing: null, reason: '' })} disabled={revertingSold}>Cancel</Button>
            <Button onClick={confirmRevertSold} disabled={revertingSold} data-testid='mod-revert-sold-confirm'>
              {revertingSold ? 'Reverting…' : 'Revert to Live'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verification rejection dialog. Reason is MANDATORY — the message
          appears in the user's Verification Center so they know what to
          fix before re-submitting the document. */}
      <Dialog open={verifyRejectModal.open} onOpenChange={(o) => !o && setVerifyRejectModal({ open: false, id: '', reason: '' })}>
        <DialogContent className='max-w-lg' data-testid='mod-verify-reject-dialog'>
          <DialogHeader>
            <DialogTitle>Reject verification</DialogTitle>
            <DialogDescription>
              Explain what&apos;s wrong with the uploaded document. This message
              is shown to the user in their Verification Center so they
              know exactly what to correct.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label>Rejection reason <span className='text-red-600'>*</span></Label>
            <Textarea
              rows={4}
              value={verifyRejectModal.reason}
              onChange={(e) => setVerifyRejectModal(s => ({ ...s, reason: e.target.value }))}
              placeholder='e.g. GST document is blurry / GSTIN on the certificate does not match your profile / File is expired…'
              data-testid='mod-verify-reject-reason'
            />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setVerifyRejectModal({ open: false, id: '', reason: '' })} disabled={verifyBusy}>Cancel</Button>
            <Button
              variant='destructive'
              onClick={confirmVerifyReject}
              disabled={verifyBusy || !(verifyRejectModal.reason || '').trim()}
              data-testid='mod-verify-reject-confirm'
            >
              {verifyBusy ? 'Rejecting…' : 'Reject & notify user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModeratorPanel;
